import { endpoint } from '~/api';
import { ID } from '~/lib/model';
import { DateRange, toUTCWithLocalTime } from '~/lib/date';
import { IPayment, PaymentStatus } from '~/modules/Payments/models/Payment';
import { IPaginator } from '~/lib/paginator';
import { IPaymentTransactionHistory } from '~/modules/Payments/models/Payment';
import { IFinanceSummary } from '~/modules/Payments/models/FinanceSummary';

export const fetchPayments = (sellerId: ID = null) => {
    const url = sellerId ? `/payment/account/${sellerId}` : '/payment';
    return endpoint.get<IPayment[]>(url).then(r => r.data);
};

export const createPayment = (accountId: ID) => endpoint
    .post(`/payment/account/${accountId}`).then(r => r.data);

export const updatePaymentStatus = (paymentId: string, status: PaymentStatus, reason?: string) =>
    endpoint.post(`/payment/${paymentId}/status`, {
        status,
        denyReason: reason
    }).then(r => r.data);

export interface IPaymentsTransactionHistoryFilter {
    sellerIds: number[];
    transactionType?: string;
    dateRange?: DateRange;
    creationDateRange?: DateRange;
    onlyPayouts?: boolean;
    net?: string;
}

interface ICreativePaginator {
    pageNo?: number;
    pageSize?: number
}

export const fetchPaymentsTransactionHistory = (filter: IPaymentsTransactionHistoryFilter, paginator: ICreativePaginator) => {
    const params = new URLSearchParams();

    for (let sid of filter.sellerIds) {
        params.append('accountId', sid.toString());
    }
    if (filter.net) {
        params.set('net', filter.net.toString());
    }
    if (filter.transactionType) {
        params.set('transactionType', filter.transactionType.toString());
    }
    if (filter.dateRange?.startDate) {
        params.set('dateFrom', toUTCWithLocalTime(filter.dateRange.startDate).toISOString());
    }
    if (filter.dateRange?.endDate) {
        params.set('dateTo', toUTCWithLocalTime(filter.dateRange.endDate).toISOString());
    }
    if (filter.creationDateRange?.startDate) {
        params.set('creationDateFrom', toUTCWithLocalTime(filter.creationDateRange.startDate).toISOString());
    }
    if (filter.creationDateRange?.endDate) {
        params.set('creationDateTo', toUTCWithLocalTime(filter.creationDateRange.endDate).toISOString());
    }
    if (filter.onlyPayouts !== undefined) {
        params.set('onlyPayouts', filter.onlyPayouts.toString());
    }
    params.set('pageNo', paginator.pageNo.toString());
    params.set('pageSize', paginator.pageSize.toString());

    return endpoint.get<IPaginator<IPaymentTransactionHistory>>(`/transactions?${params.toString()}`).then(r => r.data);
}

export interface IFinanceSummaryFilter {
    accountIds: number[];
    net?: string;
    nextReleaseDate?: string;
}

export const fetchFinanceSummary = (filter: IFinanceSummaryFilter, paginator: ICreativePaginator) => {
    const params = new URLSearchParams();

    for (let sid of filter.accountIds) {
        params.append('accountId', sid.toString());
    }
    if (filter.net) {
        params.set('net', filter.net.toString());
    }
    if (filter.nextReleaseDate) {
        params.set('nextReleaseDate', filter.nextReleaseDate);
    }
    params.set('pageNo', paginator.pageNo.toString());
    params.set('pageSize', paginator.pageSize.toString());

    return endpoint.get<IPaginator<IFinanceSummary>>(`/transactions/finance?${params.toString()}`).then(r => r.data);
}

interface ITrolleyWidgetInfo {
    url: string
}

export const createTrolleyUrl = (sellerID: number) => {
    return endpoint.get<ITrolleyWidgetInfo>(`trolley/widget/${sellerID}`).then(r => r.data);
}

export interface IFinancePayment {
    id: string;
    recipient: string;
    status: string;
    sourceAmount: string;
    memo: string;
    processedAt: string | null;
}

export interface IFinanceTransaction {
    payments: IFinancePayment[];
}

export const fetchFinanceTransaction = async (id: ID): Promise<IFinanceTransaction> => {
    try {
        const res = await endpoint.get<IFinanceTransaction>(`/transactions/finance/${id}`);
        return res.data;
    } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
            console.warn('[finance] cannot load /transactions/finance/', id, 'status=', status);
            return { payments: [] };
        }
        throw err;
    }
};

export const fetchInvoicePdfByPayment = (sellerId: ID, payment: IFinancePayment) =>
    endpoint
        .post(`/invoice/seller/${sellerId}`, payment, { responseType: 'blob' })
        .then((r) => r.data as Blob);
